import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Util } from '@libs/utilities/util';

import { NgAbstractInputComponent } from '../../shared/inputs.abstract';
import { EnumNgFaStyle } from '../../../../styling-components';
import { INgCheckboxDropdownMultiSelect, INgDropdownOptionsData } from './checkbox-multi-select-dropdown.interface';


@Component({
    selector: 'ng-checkbox-multi-select-dropdown',
    templateUrl: './checkbox-multi-select-dropdown.component.html',
    styleUrls: ['./checkbox-multi-select-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgCheckboxMultiSelectDropdownComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgCheckboxMultiSelectDropdownComponent extends NgAbstractInputComponent<string[]> implements ControlValueAccessor {

	@Input() dropdownWidth 		 : number = 200;
	@Input() dropdownPlacement 	 : string = 'auto';
	@Input() dropdownData 		!: INgCheckboxDropdownMultiSelect;

	//** Configurations */
	readonly DROPDOWN_DEFAULT_NAME: string = 'Select';

	//** Getters and Setters Helpers */
	readonly EnumNgFaStyle: typeof EnumNgFaStyle = EnumNgFaStyle;
	// Keep body-mounted tooltips aligned with this dropdown while the page scrolls.
	readonly tooltipPopperOptions = (options: Partial<import('@popperjs/core').Options>): Partial<import('@popperjs/core').Options> => ({ ...options, strategy: 'fixed' });

	//** Helper Variables */
	dropdownName: string = this.DROPDOWN_DEFAULT_NAME;

	constructor() {

		// component name
		super('checkbox-multi-select-dropdown');
	}

	//** Update the dropdown list name */
	updateDropdownName(): void {

		// create dropdown name
		this.dropdownName = this.value?.join(', ').toLowerCase() || '';
		if (Util.String.isEmpty(this.dropdownName)) this.dropdownName = this.DROPDOWN_DEFAULT_NAME;
	}


	/**------------------------------------------------------
	 * Control Value Accessor Functions
	 */
	override writeValue(value: string[]): void {

		//0 - was any value defined?
		if (!value) return;

		//1 - assign the values
		this.value = value;
		for (const dropdownMenu of this.dropdownData.options) {

			//a. are options existing?
			if (Util.Array.isEmpty(dropdownMenu.options)) continue;

			//b. if existing then check when value is selected
			for (const option of dropdownMenu.options) {
				option.selected = value.includes(option.code);
			}
		}

		//2 - update dropdown name
		this.updateDropdownName();
	}


	/**------------------------------------------------------
	 * User Actions
	 */
	updateSelected(): void {

		//0 - get the selected values
		const value: string[] = [];
		for (const checkboxes of this.dropdownData.options) {
			value.push(...checkboxes.options.filter((dropdownOptionsData: INgDropdownOptionsData) => dropdownOptionsData.selected).map((dropdownOptionsData: INgDropdownOptionsData) => dropdownOptionsData.code));
		}

		//1 - trigger the change with the new value and update dropdown name
		this.value = value;
		this.onChange(this.value);
		this.updateDropdownName();
	}
}
